import React, {useMemo, useState, useEffect} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {Link, useNavigate} from 'react-router-dom';

import {DataGrid, KeenIcon} from '@/components';
import {JobDetailsData} from '@/pages/buddy/types/JobDetailsData';
import {MatchDetailsData} from '@/pages/buddy/types/MatchDetailsData';
import {Loading} from '@/layouts/buddy/loading';
import {formatDate} from "@/pages/buddy/utils/Date.tsx";
import {getProgressColor} from '../../utils/ProgressColor';

interface JobsProps {
    jobs: JobDetailsData[];
    loading: boolean;
}

const Jobs: React.FC<JobsProps> = ({jobs, loading}) => {
    const storageFilterId = 'jobs-filter';
    const [isActiveFilter, setIsActiveFilter] = useState(true);

    const navigate = useNavigate();

    // Search term state
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem(storageFilterId) || '';
    });

    // Save search term to local storage
    useEffect(() => {
        localStorage.setItem(storageFilterId, searchTerm);
    }, [searchTerm]);

    const columns = useMemo<ColumnDef<JobDetailsData>[]>(
        () => [
            {
                accessorFn: (row) => row.name,
                id: 'name',
                header: () => 'Job',
                enableSorting: true,
                cell: (info) => (
                    <div className="flex flex-col">
                        <Link
                            className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
                            to={`/job/${info.row.original.p_id}`}
                        >
                            {info.row.original.name}
                        </Link>
                    </div>
                ),
            },
            {
                accessorFn: (row) => row.company_name,
                id: 'company_name',
                header: () => 'Company',
                enableSorting: true,
                cell: (info) => (
                    <div className="flex text-sm font-normal flex-col gap-2">
                        <Link
                            className="leading-none hover:text-primary"
                            to={`/company/${info.row.original.company_p_id}`}
                        >
                            {info.row.original.company_name}
                        </Link>
                    </div>
                ),
            },
            {
                accessorFn: (row) => row.matches?.[0] as MatchDetailsData | undefined,
                id: 'best_match',
                header: () => 'Best Match',
                enableSorting: false,
                cell: (info) => {
                    const match = info.row.original.matches?.[0];
                    if (!match) return 'No Matches';

                    const value = match.overall_soft_score_relative;
                    const percentage = value !== undefined && value !== null ? Math.round(value * 100) : null;

                    return (
                        <div className="flex flex-col gap-1 text-sm font-normal">
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn btn-sm btn-icon btn-clear btn-light"
                                    onClick={() => navigate(`/match/${match.p_id}`)}
                                >
                                    <KeenIcon icon="eye"/>
                                </button>
                                <Link
                                    className="leading-none hover:text-primary"
                                    to={`/match/${match.p_id}`}
                                >
                                    {match.candidate_first_name} {match.candidate_last_name}
                                </Link>
                                {percentage !== null && (
                                    <span className="text-gray-600">{percentage}%</span>
                                )}
                            </div>
                            {percentage !== null && (
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                    <div
                                        className="h-1.5 rounded-full"
                                        style={{
                                            width: `${percentage}%`,
                                            backgroundColor: getProgressColor(percentage),
                                        }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'all-candidates',
                header: () => 'Matched Candidates',
                enableSorting: false,
                cell: ({row}) => (
                    <div className="flex text-sm font-normal items-center gap-2">
                        <button
                            className="btn btn-sm btn-icon btn-clear btn-light"
                            onClick={() => navigate(`/job/${row.original.p_id}/candidates`)}
                        >
                            <KeenIcon icon="eye"/>
                        </button>
                        <Link
                            className="leading-none hover:text-primary"
                            to={`/job/${row.original.p_id}/candidates`}
                        >
                            {row.original.matches ? row.original.matches.length : 0} Candidates
                        </Link>
                    </div>
                ),
            },
            {
                accessorFn: (row) => row.updated_at,
                id: 'lastModified',
                enableSorting: true,
                header: () => 'Last Modified',
                cell: (info) => {
                    const value = info.getValue() as string | null;
                    return formatDate(value);
                },
                meta: {
                    className: 'w-[350px]',
                    cellClassName: 'text-gray-700 font-normal',
                },
            },
            {
                id: 'edit',
                header: () => 'Open',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => navigate(`/job/${row.original.p_id}`)}
                    >
                        <KeenIcon icon="notepad-edit"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },
            {
                id: 'delete',
                header: () => 'Delete',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => alert(`Clicked on delete for ${row.original.name}`)}
                    >
                        <KeenIcon icon="trash"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },
        ],
        [navigate]
    );

    const filteredData = useMemo(() => {
        return jobs.filter((job) => {
            const matchesSearch =
                (job.name ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (job.company_name ?? "").toLowerCase().includes(searchTerm.toLowerCase());
            const matchesActiveFilter = !isActiveFilter || job.is_active;
            return matchesSearch && matchesActiveFilter;
        });
    }, [jobs, searchTerm, isActiveFilter]);

    if (loading) return <Loading loading={loading}/>;


    return (
        <>
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-5">
                    <h3 className="card-title">Jobs</h3>
                    <div className="flex gap-6">
                        <div className="relative">
                            <KeenIcon
                                icon="magnifier"
                                className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                            />
                            <input
                                type="text"
                                placeholder="Search Jobs"
                                className="input input-sm pl-8"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <label className="switch switch-sm">
                            <input
                                name="check"
                                type="checkbox"
                                checked={isActiveFilter}
                                onChange={() => setIsActiveFilter(!isActiveFilter)}
                                className="order-2"
                            />
                            <span className="switch-label order-1">Only Active Jobs</span>
                        </label>
                    </div>
                </div>
                <div className="card-body">
                    <DataGrid
                        columns={columns}
                        data={filteredData}
                        pagination={{size: 10}}
                        sorting={[{id: 'name', desc: false}]}
                    />
                </div>
            </div>
        </>
    );
};

export {Jobs};
